import React, { useCallback, useState } from 'react'
import { Dropdown } from 'react-bootstrap'

import { convertUTCtoMSK } from '../../../../../../../utils'
import { MatchingFile } from '../../../../core/_models'
import { ReceiptCell } from '../../../../components/common/ReceiptCell'
import { DisputeAutoCancelCountdown } from './DisputeAutoCancelCountdown'

import { KTIcon } from '../../../../../../../_metronic/helpers'
import { RowKebabMenu, RowKebabSlot } from '../../../../../../common/RowKebabMenu'
import { TruncatedValue } from '../../../../../../common/TruncatedValue'
import { ReleaseDepositChunkModal } from '../../../../components/common/ReleaseDepositChunkModal'
import { DisputeRecord, DisputeReasonLookupItem } from '../../core/response.types'
import { DISPUTE_STATUS_BADGE, DISPUTE_STATUS_LABEL } from '../../core/consts'
import {
  isPayoutDispute,
  renderAmount,
  renderAutoSituation,
  renderChunkHold,
  renderComment,
  renderCreatedBy,
  renderCulprits,
  renderDisputeType,
  renderResolvedBy,
} from '../../core/render'
import { PayoutTokenLink } from '../../../../../../common/PayoutTokenLink'
import { DisputeReasonCell } from './DisputeReasonCell'
import { receiptDecisionOpen } from '../../../../core/receipts'

interface Props {
  dispute: DisputeRecord
  canManageBills: boolean
  onFilesChange: (disputeId: number, files: MatchingFile[]) => void
  onResolveSuccess: (dispute: DisputeRecord) => void
  onReject: (dispute: DisputeRecord) => void
  onChunkReleased: (disputeId: number, heldUntil: string) => void
  reasons: DisputeReasonLookupItem[]
  onDisputeUpdated: (dispute: DisputeRecord) => void
}

export const DisputeRow: React.FC<Props> = ({
  dispute,
  canManageBills,
  onFilesChange,
  onResolveSuccess,
  onReject,
  onChunkReleased,
  reasons,
  onDisputeUpdated,
}) => {
  const files = dispute.files

  const [showReleaseModal, setShowReleaseModal] = useState(false)
  // Своего флага «уже освободили» у строки нет: и колонка «Освобождение выплаты», и наличие
  // действия считаются из одного момента, который после освобождения обновляет владелец списка.
  // Отдельный локальный флаг разошёлся бы с колонкой и заодно сделал бы отложенное освобождение
  // неотзываемым — пока момент не наступил, удержание ещё живо и срок можно задать заново.
  const canReleaseChunk = dispute.canReleaseChunk === true

  // Открытый диспут морозит операции с чеком: бэкенд отвечает 409 TICKET_HAS_OPEN_DISPUTE,
  // пока решение по спору не принято (CPAYMENT-1615). Кнопки решения показываем только
  // на закрытых диспутах, иначе они гарантированно мёртвые.
  const canDecideBill = canManageBills && dispute.status !== 'OPEN' && receiptDecisionOpen(dispute.ticketStatus)

  const onReceiptUploaded = useCallback(
    (file: MatchingFile) => onFilesChange(dispute.id, [file]),
    [onFilesChange, dispute.id]
  )
  // Строка журнала остаётся на месте: решение по чеку не закрывает диспут
  // и не меняет статус тикета.
  // The cell hands over the receipts it shows — after a replacement those are not `dispute.files`.
  const onReceiptDecided = useCallback(
    (_billId: MatchingFile['id'], _status: MatchingFile['status'], shown: MatchingFile[]) =>
      onFilesChange(dispute.id, shown),
    [onFilesChange, dispute.id]
  )

  // Спор о выплате не замораживает чеков и не держит выплату — у его строки нет ни чека, ни удержания.
  const isPayout = isPayoutDispute(dispute)

  return (
    <tr>
      <td>
        <p className='mb-0 text-monospace'>
          {isPayout ? <PayoutTokenLink token={dispute.token} className='text-primary text-monospace' /> : dispute.token}
        </p>
        {/* Merchant-controlled length — TruncatedValue keeps the column from stretching. */}
        <div className='d-flex-column mt-2'>
          <p className='fw-bold fs-9 mb-0'>ID тикета мерчанта:</p>
          <span className='fs-9'>
            <TruncatedValue value={dispute.merchantTransactionId} className='text-monospace' empty='—' />
          </span>
        </div>
      </td>
      <td className='text-nowrap'>{renderDisputeType(dispute)}</td>
      <td>{dispute.merchantTitle ?? '—'}</td>
      <td>{convertUTCtoMSK(dispute.startedAt)}</td>
      <td>{renderAmount(dispute)}</td>
      <td>
        <DisputeReasonCell dispute={dispute} reasons={reasons} onUpdated={onDisputeUpdated} />
      </td>
      <td className='text-muted'>{renderAutoSituation(dispute)}</td>
      <td>
        <span className={`badge ${DISPUTE_STATUS_BADGE[dispute.status] ?? 'badge-light'}`}>
          {DISPUTE_STATUS_LABEL[dispute.status] ?? dispute.status}
        </span>
      </td>
      <td>
        <span className='badge badge-light'>{dispute.ticketStatus}</span>
      </td>
      <td>{renderCulprits(dispute)}</td>
      <td className='text-nowrap'>{renderChunkHold(dispute)}</td>
      <td className='text-nowrap'>
        <DisputeAutoCancelCountdown autoCloseAt={dispute.autoCloseAt} />
      </td>
      <td className='text-center'>
        {isPayout ? (
          <span className='text-muted'>—</span>
        ) : (
          <ReceiptCell
            ticketToken={dispute.token}
            files={files}
            merchantId={null}
            canManageBills={canManageBills}
            showDecisionBadge
            canDecide={canDecideBill}
            onUploaded={onReceiptUploaded}
            onDecided={onReceiptDecided}
          />
        )}
      </td>
      <td className={`text-muted ${dispute.createdBy ? 'text-monospace' : ''}`}>
        {renderCreatedBy(dispute)}
      </td>
      <td className={`text-muted ${dispute.resolvedBy ? 'text-monospace' : ''}`}>
        {renderResolvedBy(dispute)}
      </td>
      <td className='text-muted'>{renderComment(dispute.comment)}</td>
      <td className='w-200px'>
        <div className='d-flex align-items-start justify-content-end gap-2'>
          {/* d-grid gap-2: обе кнопки одной ширины и не прыгают при разной длине текста. */}
          <div className='d-grid gap-2' style={{ maxWidth: 180 }}>
            {dispute.canResolveSuccess && (
              <button
                type='button'
                className='btn btn-sm btn-light-success text-nowrap'
                onClick={() => onResolveSuccess(dispute)}
              >
                Завершить успешно
              </button>
            )}
            {dispute.canResolveReject && (
              <button
                type='button'
                className='btn btn-sm btn-light-danger text-nowrap'
                onClick={() => onReject(dispute)}
              >
                Отказать
              </button>
            )}
          </div>
          {/* Слот держит ширину «⋮» и когда меню нет: иначе кнопки решения съезжают вправо. */}
          <RowKebabSlot>
            {canReleaseChunk && (
              <RowKebabMenu title='Действия по диспуту'>
                <Dropdown.Item
                  as='button'
                  type='button'
                  className='d-flex align-items-center'
                  onClick={() => setShowReleaseModal(true)}
                >
                  <KTIcon iconName='lock-3' className='fs-3 me-2' />
                  Освободить депозит
                </Dropdown.Item>
              </RowKebabMenu>
            )}
          </RowKebabSlot>
        </div>
        {showReleaseModal && (
          <ReleaseDepositChunkModal
            show={showReleaseModal}
            onClose={() => setShowReleaseModal(false)}
            depositToken={dispute.token}
            heldUntil={dispute.chunkHeldUntil ?? null}
            onReleased={(heldUntil) => onChunkReleased(dispute.id, heldUntil)}
          />
        )}
      </td>
    </tr>
  )
}
